import { useState, useEffect, useRef } from "react";

const C = {
  bg: "#070b14",
  bgCard: "#0d1424",
  bgCardAlt: "#111d2e",
  bgGlass: "rgba(15,25,50,0.7)",
  border: "#1a2d4a",
  borderHover: "#2563eb",
  text: "#dde8f8",
  textMuted: "#7a9cc4",
  textDim: "#3d5a80",
  blue: "#3b82f6",
  blueGlow: "rgba(59,130,246,0.2)",
  teal: "#0ea5e9",
  tealGlow: "rgba(14,165,233,0.2)",
  green: "#10b981",
  greenGlow: "rgba(16,185,129,0.18)",
  yellow: "#f59e0b",
  yellowGlow: "rgba(245,158,11,0.18)",
  red: "#ef4444",
  redGlow: "rgba(239,68,68,0.18)",
  purple: "#8b5cf6",
  purpleGlow: "rgba(139,92,246,0.18)",
  cyan: "#06b6d4",
  cyanGlow: "rgba(6,182,212,0.18)",
  orange: "#f97316",
  orangeGlow: "rgba(249,115,22,0.18)",
};

const FONT = `'JetBrains Mono', 'Fira Code', monospace`;
const SANS = `'Sora', 'DM Sans', 'Segoe UI', sans-serif`;

const layout = {
  app: { background: C.bg, minHeight: "100vh", color: C.text, fontFamily: SANS, fontSize: 14 },
  sidebar: { width: 240, background: C.bgCard, borderRight: `1px solid ${C.border}`, height: "calc(100vh - 70px)", position: "fixed", top: 70, left: 0, overflowY: "auto", zIndex: 100 },
  main: { marginLeft: 240, minHeight: "100vh", padding: "32px 40px", maxWidth: 1000 },
  navItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", borderRadius: 8, margin: "2px 8px", background: active ? C.blueGlow : "transparent", color: active ? C.blue : C.textMuted, fontWeight: active ? 600 : 400, fontSize: 13, transition: "all .18s", borderLeft: active ? `3px solid ${C.blue}` : "3px solid transparent" }),
  badge: (col) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: col + "22", color: col, border: `1px solid ${col}44`, letterSpacing: 0.3 }),
};

// ─── TOP-LEVEL TOPICS ─────────────────────────────────────────────────────────
const TOPICS = [
  {
    id: "auth",
    num: "01",
    icon: "🔑",
    title: "Authentication",
    subtitle: "Who are you?",
    color: C.blue,
    glow: C.blueGlow,
    analogy: {
      scene: "🏢 Office Building Security Desk",
      text: "Before entering an office, a guard checks your ID badge. Authentication is exactly that — Kubernetes asks every request: \"Can you PROVE who you are?\"",
      mapping: [
        { real: "Your ID badge", k8s: "Certificate / Token / Password" },
        { real: "Security guard", k8s: "API Server" },
        { real: "HR database", k8s: "User store / OIDC provider" },
      ],
    },
    concepts: [
      {
        term: "Certificates (X.509)",
        icon: "📜",
        color: C.blue,
        what: "A digital ID card signed by a trusted authority (CA). Most common in K8s.",
        when: "Admin users, component-to-component communication",
        cmd: "kubectl config view --minify",
      },
      {
        term: "ServiceAccount Tokens",
        icon: "🤖",
        color: C.teal,
        what: "Auto-generated tokens for PODS to talk to the API server. Like a robot employee badge.",
        when: "Your apps running inside the cluster",
        cmd: "kubectl get serviceaccount default -n default",
      },
      {
        term: "OIDC (OpenID Connect)",
        icon: "🌐",
        color: C.purple,
        what: "Login via external identity provider (Google, GitHub, Okta). Like 'Login with Google'.",
        when: "Large orgs with SSO, multi-user clusters",
        cmd: "# Configured in kube-apiserver flags",
      },
      {
        term: "Bootstrap Tokens",
        icon: "🚀",
        color: C.orange,
        what: "Temporary tokens used when new nodes join the cluster. Like a day-pass for new employees.",
        when: "kubeadm join — adding new worker nodes",
        cmd: "kubeadm token list",
      },
    ],
  },
  {
    id: "authz",
    num: "02",
    icon: "🚪",
    title: "Authorization (RBAC)",
    subtitle: "What can you do?",
    color: C.green,
    glow: C.greenGlow,
    analogy: {
      scene: "🏨 Hotel Key Card System",
      text: "After showing your ID (authentication), the hotel gives you a KEY CARD. It only opens YOUR room, not every room. RBAC is the system that decides which doors each key can open.",
      mapping: [
        { real: "Key card permissions", k8s: "Role / ClusterRole" },
        { real: "Assigning card to guest", k8s: "RoleBinding / ClusterRoleBinding" },
        { real: "Which rooms you can enter", k8s: "Verbs on Resources (get, list, delete)" },
      ],
    },
    concepts: [
      {
        term: "Role",
        icon: "📋",
        color: C.green,
        what: "A set of PERMISSIONS scoped to a specific namespace. Like 'can only access Floor 3 rooms'.",
        when: "Team-level access to their own namespace",
        cmd: "kubectl create role pod-reader --verb=get,list --resource=pods -n myapp",
      },
      {
        term: "ClusterRole",
        icon: "🌍",
        color: C.teal,
        what: "Same as Role but cluster-wide — crosses all namespaces. Like 'master key for the whole hotel'.",
        when: "Admins, monitoring tools that need cross-namespace access",
        cmd: "kubectl get clusterroles",
      },
      {
        term: "RoleBinding",
        icon: "🔗",
        color: C.yellow,
        what: "Ties a Role to a User/ServiceAccount. The act of GIVING someone the key card.",
        when: "Granting a developer access to their namespace",
        cmd: "kubectl create rolebinding dev-binding --role=pod-reader --user=alice -n myapp",
      },
      {
        term: "ClusterRoleBinding",
        icon: "⛓️",
        color: C.purple,
        what: "Ties a ClusterRole to a subject, cluster-wide. Like making someone the hotel general manager.",
        when: "Cluster admins, system components",
        cmd: "kubectl create clusterrolebinding alice-admin --clusterrole=admin --user=alice",
      },
    ],
  },
  {
    id: "admctrl",
    num: "03",
    icon: "🛂",
    title: "Admission Controllers",
    subtitle: "Should this request be allowed?",
    color: C.yellow,
    glow: C.yellowGlow,
    analogy: {
      scene: "✈️ Airport Security + Customs",
      text: "You've shown your passport (auth) and have a valid visa (RBAC) — but customs STILL checks your bags. Admission Controllers are the final checkpoint BEFORE anything is stored in the cluster.",
      mapping: [
        { real: "X-ray scanner", k8s: "Validating Admission Controller" },
        { real: "Duty-free limits applied", k8s: "Mutating Admission Controller" },
        { real: "Customs officer", k8s: "Admission Webhook" },
      ],
    },
    concepts: [
      {
        term: "Mutating Admission",
        icon: "✏️",
        color: C.yellow,
        what: "CAN MODIFY the request before it's saved. Like customs stamping your passport WITH a new entry date.",
        when: "Injecting sidecars, adding default labels, setting resource limits",
        cmd: "# Example: Istio auto-injects sidecar via MutatingWebhook",
      },
      {
        term: "Validating Admission",
        icon: "✅",
        color: C.green,
        what: "Can ACCEPT or REJECT the request but cannot modify it. Like a form validator.",
        when: "Enforcing policies: no root containers, must have resource limits",
        cmd: "kubectl get validatingwebhookconfigurations",
      },
      {
        term: "LimitRanger",
        icon: "📏",
        color: C.teal,
        what: "Built-in controller that enforces resource limits per namespace. Prevents runaway pods.",
        when: "Multi-tenant clusters, ensuring fairness",
        cmd: "kubectl describe limitrange -n myapp",
      },
      {
        term: "PodSecurity Admission",
        icon: "🛡️",
        color: C.purple,
        what: "Enforces Pod Security Standards (Privileged/Baseline/Restricted). Replaced old PodSecurityPolicy.",
        when: "Preventing pods from running as root or with host access",
        cmd: "kubectl label namespace myapp pod-security.kubernetes.io/enforce=restricted",
      },
    ],
  },
  {
    id: "podsec",
    num: "04",
    icon: "🔒",
    title: "Pod Security",
    subtitle: "Is this container safe?",
    color: C.purple,
    glow: C.purpleGlow,
    analogy: {
      scene: "🏠 Tenant Rules in an Apartment Building",
      text: "A building has rules for tenants: no loud music after 10pm, no pets on the roof, no modifying electrical wiring. Pod Security is the rulebook for what containers can and cannot do inside the cluster.",
      mapping: [
        { real: "No modifying electrical wiring", k8s: "No privileged containers" },
        { real: "No accessing neighbor's apartment", k8s: "No hostPID / hostNetwork" },
        { real: "Must use approved appliances", k8s: "Must use specific securityContext" },
      ],
    },
    concepts: [
      {
        term: "securityContext",
        icon: "🔐",
        color: C.purple,
        what: "Per-pod or per-container security settings. runAsUser, runAsNonRoot, readOnlyRootFilesystem.",
        when: "Every production pod should set these",
        cmd: "# In pod spec:\n# securityContext:\n#   runAsNonRoot: true\n#   readOnlyRootFilesystem: true",
      },
      {
        term: "Pod Security Standards",
        icon: "📊",
        color: C.blue,
        what: "3 built-in levels: Privileged (no limits), Baseline (some protections), Restricted (hardened).",
        when: "Choosing the right security posture per namespace",
        cmd: "# Privileged → dev/testing\n# Baseline → most workloads\n# Restricted → sensitive workloads",
      },
      {
        term: "Capabilities",
        icon: "⚡",
        color: C.orange,
        what: "Linux kernel powers a container can use. Drop ALL capabilities, only add what's needed (principle of least privilege).",
        when: "Fine-grained control over what a container can do at OS level",
        cmd: "# capabilities:\n#   drop: [\"ALL\"]\n#   add: [\"NET_BIND_SERVICE\"]",
      },
      {
        term: "AppArmor / Seccomp",
        icon: "🛡️",
        color: C.teal,
        what: "OS-level security profiles that restrict which system calls a container can make.",
        when: "Defense-in-depth for high security workloads",
        cmd: "# seccompProfile:\n#   type: RuntimeDefault",
      },
    ],
  },
  {
    id: "netpol",
    num: "05",
    icon: "🌐",
    title: "Network Policies",
    subtitle: "Who can talk to whom?",
    color: C.teal,
    glow: C.tealGlow,
    analogy: {
      scene: "🏙️ City Road Rules & Checkpoints",
      text: "By default, ANY pod can talk to ANY pod — like a city with NO traffic rules. Network Policies are like installing checkpoints and one-way roads between neighborhoods.",
      mapping: [
        { real: "City checkpoints", k8s: "NetworkPolicy rules" },
        { real: "One-way streets", k8s: "Ingress / Egress rules" },
        { real: "Allowed neighborhoods", k8s: "namespaceSelector / podSelector" },
      ],
    },
    concepts: [
      {
        term: "Default Deny All",
        icon: "🚫",
        color: C.red,
        what: "A NetworkPolicy that blocks ALL traffic to/from a namespace. Then you explicitly allow only what's needed.",
        when: "Starting point for any secure namespace",
        cmd: "# spec:\n#   podSelector: {}\n#   policyTypes: [Ingress, Egress]",
      },
      {
        term: "Ingress Rules",
        icon: "📥",
        color: C.green,
        what: "Controls INCOMING traffic to pods. 'Who can send requests to my pod?'",
        when: "Only allow frontend pods to reach backend pods",
        cmd: "# from:\n#   - podSelector:\n#       matchLabels:\n#         role: frontend",
      },
      {
        term: "Egress Rules",
        icon: "📤",
        color: C.blue,
        what: "Controls OUTGOING traffic from pods. 'Where can my pod send requests?'",
        when: "Prevent data exfiltration, restrict internet access",
        cmd: "# to:\n#   - namespaceSelector:\n#       matchLabels:\n#         name: kube-system",
      },
      {
        term: "CNI Plugin Requirement",
        icon: "🔌",
        color: C.yellow,
        what: "Network Policies only work if your CNI plugin supports them (Calico, Cilium, Weave). Vanilla flannel does NOT enforce them.",
        when: "Always verify your CNI supports NetworkPolicy enforcement",
        cmd: "# Popular choices: Calico, Cilium (also has eBPF)",
      },
    ],
  },
  {
    id: "secrets",
    num: "06",
    icon: "🗝️",
    title: "Secrets Management",
    subtitle: "Storing sensitive data safely",
    color: C.orange,
    glow: C.orangeGlow,
    analogy: {
      scene: "🏦 Bank Vault with Access Logs",
      text: "You wouldn't tape your bank PIN to your front door. Secrets are like a bank vault — the sensitive data is locked away, and only authorized people (or pods) get the key, and every access is logged.",
      mapping: [
        { real: "Bank vault", k8s: "etcd (Secrets store)" },
        { real: "Vault access permission", k8s: "RBAC on Secret resources" },
        { real: "Security deposit box key", k8s: "Volume mount / env var injection" },
      ],
    },
    concepts: [
      {
        term: "K8s Secrets",
        icon: "🔑",
        color: C.orange,
        what: "Base64-encoded (NOT encrypted by default!) key-value store for sensitive data like passwords and API keys.",
        when: "Quick secrets for non-critical data, or when encryption-at-rest is enabled",
        cmd: "kubectl create secret generic db-pass --from-literal=password=mypass",
      },
      {
        term: "Encryption at Rest",
        icon: "💾",
        color: C.purple,
        what: "Configures etcd to actually ENCRYPT secrets on disk. Critical for production clusters.",
        when: "Any production cluster — secrets are plain base64 without this!",
        cmd: "# EncryptionConfiguration in kube-apiserver\n# providers: [aescbc, secretbox]",
      },
      {
        term: "External Secret Stores",
        icon: "☁️",
        color: C.teal,
        what: "Use Vault (HashiCorp), AWS Secrets Manager, or Azure Key Vault for enterprise-grade secrets.",
        when: "Production, regulated environments, secret rotation needs",
        cmd: "# Tools: External Secrets Operator\n# secrets-store-csi-driver",
      },
      {
        term: "Avoid Env Vars for Secrets",
        icon: "⚠️",
        color: C.red,
        what: "Env vars are visible in process listings and logs. Prefer volume mounts so secrets are files, not exposed variables.",
        when: "Security best practice — use volumeMount instead of env",
        cmd: "# volumeMounts:\n#   - name: secret-vol\n#     mountPath: /etc/secrets\n#     readOnly: true",
      },
    ],
  },
  {
    id: "etcd",
    num: "07",
    icon: "🗄️",
    title: "etcd Security",
    subtitle: "Protecting the brain's database",
    color: C.red,
    glow: C.redGlow,
    analogy: {
      scene: "🧠 The Master Blueprint Room",
      text: "etcd holds the ENTIRE desired state of your cluster — every secret, every config, every deployment spec. It's the master blueprint room. If someone gets etcd access, they own everything.",
      mapping: [
        { real: "Blueprint room door lock", k8s: "TLS encryption + certificates" },
        { real: "Only architects allowed", k8s: "etcd RBAC / network isolation" },
        { real: "Photocopy of blueprints offsite", k8s: "etcd backups" },
      ],
    },
    concepts: [
      {
        term: "TLS for etcd",
        icon: "🔒",
        color: C.red,
        what: "All communication to/from etcd must be TLS encrypted. Never run etcd without TLS in production.",
        when: "Always — this is the default in kubeadm-bootstrapped clusters",
        cmd: "# etcd started with:\n# --cert-file, --key-file\n# --peer-cert-file, --peer-key-file",
      },
      {
        term: "etcd Backup",
        icon: "📸",
        color: C.orange,
        what: "Regular snapshots of etcd data. If your control plane dies, this is how you restore everything.",
        when: "Before upgrades, and on a scheduled basis (every 30min in prod)",
        cmd: "ETCDCTL_API=3 etcdctl snapshot save /backup/etcd.db\n--endpoints=https://127.0.0.1:2379",
      },
      {
        term: "Network Isolation",
        icon: "🔌",
        color: C.purple,
        what: "etcd port (2379) should ONLY be accessible to the API server, not to worker nodes or users.",
        when: "Firewall rules / security groups in cloud environments",
        cmd: "# Port 2379 (client) and 2380 (peer)\n# Should be firewalled from workers and public internet",
      },
      {
        term: "Least Privilege for etcd",
        icon: "🎯",
        color: C.blue,
        what: "Only the API server should read/write etcd. No direct human access to etcd in production.",
        when: "Production hardening",
        cmd: "# Access etcd only via kubectl (API server as proxy)\n# Direct etcd access requires the CA cert",
      },
    ],
  },
];

// ─── RBAC SIMULATOR ──────────────────────────────────────────────────────────
const RBAC_SCENARIOS = [
  { user: "alice (Developer)", icon: "👩‍💻", role: "pod-reader", ns: "myapp", verb: "get", resource: "pods", allowed: true },
  { user: "alice (Developer)", icon: "👩‍💻", role: "pod-reader", ns: "myapp", verb: "delete", resource: "deployments", allowed: false },
  { user: "bob (DevOps)", icon: "👨‍🔧", role: "namespace-admin", ns: "myapp", verb: "create", resource: "deployments", allowed: true },
  { user: "ci-bot (ServiceAccount)", icon: "🤖", role: "deployer", ns: "myapp", verb: "update", resource: "deployments", allowed: true },
  { user: "ci-bot (ServiceAccount)", icon: "🤖", role: "deployer", ns: "myapp", verb: "get", resource: "secrets", allowed: false },
  { user: "eve (Attacker)", icon: "😈", role: "none", ns: "kube-system", verb: "get", resource: "secrets", allowed: false },
];

function RBACSimulator() {
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const s = RBAC_SCENARIOS[idx];

  const next = () => { setIdx((idx + 1) % RBAC_SCENARIOS.length); setRevealed(false); };
  const prev = () => { setIdx((idx - 1 + RBAC_SCENARIOS.length) % RBAC_SCENARIOS.length); setRevealed(false); };

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, marginBottom: 32 }}>
      <div style={{ fontFamily: FONT, fontSize: 12, color: C.cyan, letterSpacing: 2, marginBottom: 4 }}>
        RBAC ACCESS CHECKER
      </div>
      <p style={{ fontFamily: SANS, fontSize: 13, color: C.textMuted, marginBottom: 20 }}>
        Guess whether this request will be <strong style={{ color: C.green }}>ALLOWED</strong> or <strong style={{ color: C.red }}>DENIED</strong>
      </p>

      <div style={{ background: C.bg, borderRadius: 10, padding: 20, marginBottom: 16, border: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 32 }}>{s.icon}</span>
          <div>
            <div style={{ fontFamily: FONT, fontSize: 14, color: C.text }}>{s.user}</div>
            <div style={{ fontFamily: SANS, fontSize: 12, color: C.textMuted }}>Role: <span style={{ color: C.yellow }}>{s.role}</span></div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            { label: "ACTION", value: s.verb, color: C.blue },
            { label: "RESOURCE", value: s.resource, color: C.purple },
            { label: "NAMESPACE", value: s.ns, color: C.teal },
          ].map((item, i) => (
            <div key={i} style={{ background: C.bgCardAlt, border: `1px solid ${item.color}44`, borderRadius: 6, padding: "6px 12px" }}>
              <div style={{ fontFamily: FONT, fontSize: 9, color: C.textDim, letterSpacing: 1 }}>{item.label}</div>
              <div style={{ fontFamily: FONT, fontSize: 13, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {!revealed ? (
        <button onClick={() => setRevealed(true)} style={{
          width: "100%", padding: "12px", borderRadius: 8, border: `1px solid ${C.borderHover}`,
          background: "transparent", color: C.blue, fontFamily: FONT, fontSize: 13, cursor: "pointer", marginBottom: 12,
        }}>
          Reveal Answer →
        </button>
      ) : (
        <div style={{
          borderRadius: 10, padding: "14px 20px", marginBottom: 12, textAlign: "center",
          background: s.allowed ? C.greenGlow : C.redGlow,
          border: `2px solid ${s.allowed ? C.green : C.red}`,
        }}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>{s.allowed ? "✅" : "⛔"}</div>
          <div style={{ fontFamily: FONT, fontSize: 16, color: s.allowed ? C.green : C.red, fontWeight: 700 }}>
            {s.allowed ? "ALLOWED" : "DENIED"}
          </div>
          <div style={{ fontFamily: SANS, fontSize: 12, color: C.textMuted, marginTop: 6 }}>
            {s.allowed
              ? `${s.role} grants ${s.verb} on ${s.resource} in namespace ${s.ns}`
              : `${s.role} does NOT grant ${s.verb} on ${s.resource}`}
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button onClick={prev} style={{
          padding: "8px 16px", borderRadius: 6, border: `1px solid ${C.border}`,
          background: "transparent", color: C.textMuted, fontFamily: FONT, fontSize: 12, cursor: "pointer",
        }}>← Prev</button>
        <span style={{ fontFamily: FONT, fontSize: 11, color: C.textDim, alignSelf: "center" }}>
          {idx + 1} / {RBAC_SCENARIOS.length}
        </span>
        <button onClick={next} style={{
          padding: "8px 16px", borderRadius: 6, border: `1px solid ${C.border}`,
          background: "transparent", color: C.textMuted, fontFamily: FONT, fontSize: 12, cursor: "pointer",
        }}>Next →</button>
      </div>
    </div>
  );
}

// ─── ANIMATED PIPELINE ──────────────────────────────────────────────────────
const PIPE_STAGES = [
  { id: "request", label: "kubectl request", icon: "👤", color: C.blue, desc: "User sends a request to the API server" },
  { id: "authn", label: "Authentication", icon: "🔑", color: C.purple, desc: "Who are you? Verify certificate/token" },
  { id: "authz", label: "Authorization", icon: "🚪", color: C.green, desc: "Can you do this? Check RBAC rules" },
  { id: "admit", label: "Admission", icon: "🛂", color: C.yellow, desc: "Should we allow this? Webhooks & policies" },
  { id: "etcd", label: "etcd Store", icon: "🗄️", color: C.teal, desc: "Saved! Object persisted to etcd" },
];

function SecurityPipeline() {
  const [active, setActive] = useState(-1);
  const [blocked, setBlocked] = useState(null);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState("");
  const timerRef = useRef(null);

  const stopAll = () => { clearTimeout(timerRef.current); setRunning(false); };

  const runHappy = () => {
    stopAll(); setBlocked(null); setActive(-1); setRunning(true); setStatus("Processing request...");
    PIPE_STAGES.forEach((_, i) => {
      timerRef.current = setTimeout(() => {
        setActive(i);
        if (i === PIPE_STAGES.length - 1) {
          setStatus("✅ Request ACCEPTED — object saved to etcd");
          setRunning(false);
        }
      }, i * 900 + 300);
    });
  };

  const runBlocked = (at) => {
    stopAll(); setBlocked(null); setActive(-1); setRunning(true); setStatus("Processing request...");
    for (let i = 0; i <= at; i++) {
      const idx = i;
      timerRef.current = setTimeout(() => {
        setActive(idx);
        if (idx === at) {
          setTimeout(() => {
            setBlocked(at);
            setStatus(`❌ Request BLOCKED at ${PIPE_STAGES[at].label}`);
            setRunning(false);
          }, 700);
        }
      }, i * 900 + 300);
    }
  };

  const reset = () => { stopAll(); setActive(-1); setBlocked(null); setStatus(""); };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, marginBottom: 32 }}>
      <div style={{ fontFamily: FONT, fontSize: 12, color: C.cyan, letterSpacing: 2, marginBottom: 4 }}>
        SECURITY PIPELINE — ANIMATED
      </div>
      <p style={{ fontFamily: SANS, fontSize: 13, color: C.textMuted, marginBottom: 20 }}>
        Every kubectl command passes through these 3 gates before touching etcd.
      </p>

      {/* Pipeline viz */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 24, overflowX: "auto", paddingBottom: 8 }}>
        {PIPE_STAGES.map((stage, i) => {
          const isActive = active === i;
          const isBlocked = blocked === i;
          const isDone = active > i && blocked === null;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{
                background: isBlocked ? C.redGlow : isActive ? `${stage.color}22` : isDone ? C.greenGlow : C.bgCardAlt,
                border: `2px solid ${isBlocked ? C.red : isActive ? stage.color : isDone ? C.green : C.border}`,
                borderRadius: 10, padding: "12px 10px", textAlign: "center", minWidth: 90,
                transition: "all 0.4s",
                boxShadow: isActive ? `0 0 20px ${stage.color}55` : "none",
                transform: isActive ? "scale(1.06)" : "scale(1)",
              }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{isBlocked ? "🚫" : isDone ? "✓" : stage.icon}</div>
                <div style={{ fontFamily: FONT, fontSize: 9, color: isBlocked ? C.red : isActive ? stage.color : isDone ? C.green : C.textMuted, lineHeight: 1.3 }}>
                  {stage.label}
                </div>
              </div>
              {i < PIPE_STAGES.length - 1 && (
                <div style={{ width: 20, height: 2, background: isDone ? C.green : C.border, transition: "background 0.4s", position: "relative" }}>
                  <div style={{ position: "absolute", right: -4, top: -4, fontSize: 10, color: isDone ? C.green : C.border }}>▶</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Status */}
      {status && (
        <div style={{
          background: C.bgCardAlt, borderRadius: 8, padding: "10px 16px", marginBottom: 16,
          fontFamily: FONT, fontSize: 12, color: C.text, textAlign: "center",
        }}>
          {status}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          { label: "▶ Valid Request", action: runHappy, bg: C.green, color: "#fff" },
          { label: "Block at AuthN", action: () => runBlocked(1), bg: C.purple, color: "#fff" },
          { label: "Block at AuthZ", action: () => runBlocked(2), bg: C.yellow, color: "#000" },
          { label: "Block at Admission", action: () => runBlocked(3), bg: C.orange, color: "#fff" },
        ].map((btn, i) => (
          <button key={i} onClick={btn.action} disabled={running} style={{
            padding: "8px 14px", borderRadius: 6, border: "none",
            background: running ? C.border : btn.bg, color: running ? C.textDim : btn.color,
            fontFamily: FONT, fontSize: 11, cursor: running ? "default" : "pointer",
          }}>{btn.label}</button>
        ))}
        <button onClick={reset} style={{
          padding: "8px 14px", borderRadius: 6, border: `1px solid ${C.border}`,
          background: "transparent", color: C.textMuted, fontFamily: FONT, fontSize: 11, cursor: "pointer",
        }}>↺ Reset</button>
      </div>

      {active >= 0 && (
        <div style={{
          marginTop: 16, background: C.bg, borderRadius: 8, padding: "10px 14px",
          fontFamily: SANS, fontSize: 12.5, color: C.textMuted, lineHeight: 1.6,
        }}>
          💡 {PIPE_STAGES[Math.min(active, PIPE_STAGES.length - 1)].desc}
        </div>
      )}
    </div>
  );
}

// ─── TOPIC CARD ──────────────────────────────────────────────────────────────
function TopicSection({ topic }) {
  const [openConcept, setOpenConcept] = useState(null);
  const [tab, setTab] = useState("analogy");

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 20 }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${topic.color}15, transparent)`,
        borderBottom: `1px solid ${C.border}`, padding: "18px 22px",
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <span style={{ fontSize: 32 }}>{topic.icon}</span>
        <div>
          <div style={{ fontFamily: FONT, fontSize: 10, color: C.textDim, letterSpacing: 2 }}>
            {topic.num} — SECURITY LAYER
          </div>
          <div style={{ fontFamily: FONT, fontSize: 18, color: topic.color, fontWeight: 700 }}>{topic.title}</div>
          <div style={{ fontFamily: SANS, fontSize: 13, color: C.textMuted }}>{topic.subtitle}</div>
        </div>
      </div>

      <div style={{ padding: "20px 22px" }}>
        {/* Tab switcher */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
          {["analogy", "concepts"].map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: "6px 16px", borderRadius: 6,
              border: `1px solid ${tab === t ? topic.color : C.border}`,
              background: tab === t ? `${topic.color}20` : "transparent",
              color: tab === t ? topic.color : C.textMuted,
              fontFamily: FONT, fontSize: 11, cursor: "pointer", transition: "all 0.2s",
            }}>
              {t === "analogy" ? "🎯 Analogy" : "📚 Concepts"}
            </button>
          ))}
        </div>

        {tab === "analogy" && (
          <div>
            <div style={{
              background: `${topic.color}11`, border: `1px solid ${topic.color}44`,
              borderRadius: 10, padding: 18, marginBottom: 16,
            }}>
              <div style={{ fontFamily: FONT, fontSize: 12, color: topic.color, marginBottom: 8 }}>{topic.analogy.scene}</div>
              <p style={{ fontFamily: SANS, fontSize: 13.5, color: C.text, lineHeight: 1.7, margin: 0 }}>{topic.analogy.text}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {topic.analogy.mapping.map((m, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", background: C.bgCardAlt, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ flex: 1, fontFamily: SANS, fontSize: 12.5, color: C.textMuted }}>🏠 {m.real}</div>
                  <div style={{ color: C.textDim, fontSize: 16 }}>→</div>
                  <div style={{ flex: 1, fontFamily: FONT, fontSize: 11.5, color: topic.color }}>☸️ {m.k8s}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "concepts" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {topic.concepts.map((concept, i) => {
              const isOpen = openConcept === i;
              return (
                <div key={i} style={{
                  background: isOpen ? `${concept.color}11` : C.bgCardAlt,
                  border: `1px solid ${isOpen ? concept.color : C.border}`,
                  borderRadius: 8, overflow: "hidden", transition: "all 0.3s",
                }}>
                  <div onClick={() => setOpenConcept(isOpen ? null : i)} style={{
                    padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                  }}>
                    <span style={{ fontSize: 20 }}>{concept.icon}</span>
                    <span style={{ fontFamily: FONT, fontSize: 13, color: concept.color, flex: 1, fontWeight: 600 }}>{concept.term}</span>
                    <span style={{ fontSize: 11, color: C.textDim, transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
                  </div>
                  {isOpen && (
                    <div style={{ padding: "0 16px 14px" }}>
                      <p style={{ fontFamily: SANS, fontSize: 13, color: C.text, lineHeight: 1.65, margin: "0 0 10px" }}>{concept.what}</p>
                      <div style={{ background: C.bgCard, borderRadius: 6, padding: "8px 12px", marginBottom: 10, fontFamily: SANS, fontSize: 12, color: C.textMuted }}>
                        <span style={{ color: C.yellow }}>📌 When: </span>{concept.when}
                      </div>
                      <div style={{
                        background: "#000", borderRadius: 6, padding: "8px 12px",
                        fontFamily: FONT, fontSize: 11, color: C.green,
                        whiteSpace: "pre-wrap", lineHeight: 1.6, border: `1px solid ${C.border}`,
                      }}>
                        {concept.cmd}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── QUICK QUIZ ──────────────────────────────────────────────────────────────
const QUIZ = [
  {
    q: "A pod in namespace 'payments' needs to read secrets. What should you create?",
    opts: ["ClusterRole + ClusterRoleBinding", "Role + RoleBinding in 'payments' namespace", "Directly use the default ServiceAccount", "Add the pod to kube-system"],
    ans: 1, explain: "Always use namespaced Role + RoleBinding for namespace-scoped access. Least privilege!",
  },
  {
    q: "You want to block ALL inbound traffic to pods in namespace 'secure', then open only port 8080 from the frontend. What do you create first?",
    opts: ["An Ingress resource", "A default-deny NetworkPolicy", "A ClusterRole", "An admission webhook"],
    ans: 1, explain: "Start with a default-deny NetworkPolicy, then add an allow rule for specific traffic.",
  },
  {
    q: "etcd is exposed on port 2379. Who should be allowed to connect to it?",
    opts: ["All cluster nodes", "Only the API server", "Any admin user directly", "Worker nodes and API server"],
    ans: 1, explain: "Only the API server should talk to etcd. Direct human access is dangerous in production.",
  },
  {
    q: "A container needs to bind to port 80 (a privileged port). What's the SAFEST approach?",
    opts: ["Run the container as root", "Use CAP_NET_BIND_SERVICE capability only", "Set privileged: true", "Disable seccomp"],
    ans: 1, explain: "Drop ALL capabilities and add only the one needed. Principle of least privilege.",
  },
  {
    q: "Where does Admission Control happen in the request pipeline?",
    opts: ["Before Authentication", "Between Authentication and etcd", "After the object is saved to etcd", "Only for DELETE operations"],
    ans: 1, explain: "AuthN → AuthZ → Admission → etcd. Admission is the last gate before persistence.",
  },
];

function QuizSection() {
  const [qi, setQi] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const q = QUIZ[qi];

  const choose = (i) => {
    if (selected !== null) return;
    setSelected(i);
    if (i === q.ans) setScore(s => s + 1);
  };

  const next = () => {
    if (qi < QUIZ.length - 1) { setQi(qi + 1); setSelected(null); }
    else setDone(true);
  };

  const restart = () => { setQi(0); setSelected(null); setScore(0); setDone(false); };

  if (done) return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, textAlign: "center" }}>
      <div style={{ fontSize: 52, marginBottom: 12 }}>{score >= 4 ? "🏆" : score >= 2 ? "📚" : "💪"}</div>
      <div style={{ fontFamily: FONT, fontSize: 22, color: score >= 4 ? C.green : C.yellow, marginBottom: 8 }}>
        {score} / {QUIZ.length} correct
      </div>
      <p style={{ fontFamily: SANS, fontSize: 14, color: C.textMuted, marginBottom: 20 }}>
        {score === QUIZ.length ? "Perfect! You've mastered K8s Security basics!" : "Great effort — review the topics and try again."}
      </p>
      <button onClick={restart} style={{
        padding: "10px 24px", borderRadius: 8, border: "none",
        background: C.blue, color: "#fff", fontFamily: FONT, fontSize: 13, cursor: "pointer",
      }}>↺ Try Again</button>
    </div>
  );

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24 }}>
      <div style={{ fontFamily: FONT, fontSize: 12, color: C.cyan, letterSpacing: 2, marginBottom: 4 }}>
        KNOWLEDGE CHECK — {qi + 1}/{QUIZ.length}
      </div>
      <div style={{ height: 3, background: C.border, borderRadius: 2, marginBottom: 20, overflow: "hidden" }}>
        <div style={{
          height: "100%", background: C.blue, borderRadius: 2,
          width: `${((qi + (selected !== null ? 1 : 0)) / QUIZ.length) * 100}%`,
          transition: "width 0.4s",
        }} />
      </div>
      <p style={{ fontFamily: SANS, fontSize: 14, color: C.text, lineHeight: 1.65, marginBottom: 18 }}>{q.q}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {q.opts.map((opt, i) => {
          const isRight = i === q.ans;
          const isPicked = i === selected;
          let bg = C.bgCardAlt, border = C.border, color = C.text;
          if (selected !== null) {
            if (isRight) { bg = C.greenGlow; border = C.green; color = C.green; }
            else if (isPicked && !isRight) { bg = C.redGlow; border = C.red; color = C.red; }
          }
          return (
            <div key={i} onClick={() => choose(i)} style={{
              background: bg, border: `1px solid ${border}`, borderRadius: 8,
              padding: "12px 16px", cursor: selected === null ? "pointer" : "default",
              fontFamily: SANS, fontSize: 13, color, lineHeight: 1.5, transition: "all 0.3s",
            }}>
              {String.fromCharCode(65 + i)}. {opt}
            </div>
          );
        })}
      </div>
      {selected !== null && (
        <div style={{
          background: C.bgCardAlt, borderRadius: 8, padding: "10px 14px",
          fontFamily: SANS, fontSize: 12.5, color: C.textMuted, marginBottom: 14, lineHeight: 1.6,
        }}>
          💡 {q.explain}
        </div>
      )}
      {selected !== null && (
        <button onClick={next} style={{
          width: "100%", padding: "10px", borderRadius: 8, border: "none",
          background: C.blue, color: "#fff", fontFamily: FONT, fontSize: 13, cursor: "pointer",
        }}>
          {qi < QUIZ.length - 1 ? "Next Question →" : "See Results"}
        </button>
      )}
    </div>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
const NAV = [
  { id: "overview", icon: "🧅", label: "Overview" },
  { id: "pipeline", icon: "🔁", label: "Pipeline" },
  { id: "topics",   icon: "📚", label: "Topics" },
  { id: "rbac",     icon: "🔑", label: "RBAC Lab" },
  { id: "quiz",     icon: "🎯", label: "Quiz" },
];

export default function KubernetesSecurity() {
  const [active, setActive] = useState("overview");

  const sections = {
    overview: (
      <div>
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div style={{ fontFamily: FONT, fontSize: 12, color: C.cyan, letterSpacing: 2, marginBottom: 12 }}>THE SECURITY ONION 🧅</div>
          <p style={{ fontFamily: SANS, fontSize: 13.5, color: C.textMuted, lineHeight: 1.7, marginBottom: 16 }}>
            Kubernetes security is like an onion — multiple layers, each protecting the next. Breaking through one layer shouldn't give you everything. Here are the 7 layers we'll cover:
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {TOPICS.map((t) => (
              <div key={t.id} onClick={() => setActive("topics")} style={{
                background: C.bgCardAlt, border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${t.color}`,
                borderRadius: 8, padding: "10px 14px",
                cursor: "pointer", transition: "all 0.2s",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ fontSize: 18 }}>{t.icon}</span>
                <div>
                  <div style={{ fontFamily: FONT, fontSize: 11, color: t.color }}>{t.title}</div>
                  <div style={{ fontFamily: SANS, fontSize: 11, color: C.textDim }}>{t.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: `${C.blue}11`, border: `1px solid ${C.blue}44`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ fontFamily: FONT, fontSize: 12, color: C.blue, marginBottom: 8 }}>🏰 THE CASTLE ANALOGY</div>
          <p style={{ fontFamily: SANS, fontSize: 13.5, color: C.text, lineHeight: 1.7, margin: 0 }}>
            Think of your Kubernetes cluster as a <strong style={{ color: C.blue }}>medieval castle</strong>. The <strong style={{ color: C.purple }}>Authentication</strong> is the drawbridge guard checking IDs. <strong style={{ color: C.green }}>RBAC</strong> determines which rooms inside the castle each person can access. <strong style={{ color: C.yellow }}>Admission Controllers</strong> are like customs officers at the gate checking your cargo. <strong style={{ color: C.teal }}>Network Policies</strong> are the castle's internal doors and hallways. And <strong style={{ color: C.red }}>etcd security</strong> protects the castle's treasury where all the secrets are kept.
          </p>
        </div>
      </div>
    ),
    pipeline: <SecurityPipeline />,
    topics: <div>{TOPICS.map((topic) => <TopicSection key={topic.id} topic={topic} />)}</div>,
    rbac: (
      <div>
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ fontFamily: FONT, fontSize: 12, color: C.cyan, letterSpacing: 2, marginBottom: 8 }}>RBAC MENTAL MODEL</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { q: "Role vs ClusterRole?", a: "Role = one namespace. ClusterRole = all namespaces." },
              { q: "RoleBinding vs ClusterRoleBinding?", a: "RoleBinding = grant IN a namespace. ClusterRoleBinding = grant everywhere." },
              { q: "Can I use a ClusterRole with a RoleBinding?", a: "YES! A ClusterRole + RoleBinding = cluster role, but scoped to one namespace." },
              { q: "What is a ServiceAccount?", a: "An identity for PODS (not humans). Every pod gets one automatically." },
            ].map((item, i) => (
              <div key={i} style={{ background: C.bgCardAlt, borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontFamily: FONT, fontSize: 11.5, color: C.yellow, marginBottom: 4 }}>Q: {item.q}</div>
                <div style={{ fontFamily: SANS, fontSize: 12.5, color: C.textMuted }}>{item.a}</div>
              </div>
            ))}
          </div>
        </div>
        <RBACSimulator />
      </div>
    ),
    quiz: <QuizSection />,
  };

  return (
    <div style={layout.app}>
      <div style={layout.sidebar}>
        <div style={{ padding: "20px 16px 12px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.cyan, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>
            Kubernetes
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Security</div>
          <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
            {["RBAC", "Pod Security", "etcd"].map((t) => (
              <span key={t} style={layout.badge(C.blue)}>{t}</span>
            ))}
          </div>
        </div>
        <nav style={{ padding: "10px 0" }}>
          {NAV.map((n) => (
            <div key={n.id} style={layout.navItem(active === n.id)} onClick={() => setActive(n.id)}>
              <span style={{ fontSize: 16 }}>{n.icon}</span>
              <span>{n.label}</span>
            </div>
          ))}
        </nav>
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.6 }}>
            Pipeline:<br /><code style={{ color: C.textMuted, fontFamily: FONT }}>AuthN → AuthZ → Admission</code>
          </div>
        </div>
      </div>
      <div style={layout.main}>{sections[active]}</div>
    </div>
  );
}
